import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import type { ScheduleStatus } from '@risksmart-app/domain/src/types/consts';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import type { SchedulerScheduleRegisterItem } from '@risksmart-app/drizzle/src/dto';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_COL_WIDTH } from 'src/App.config';
import BadgeList from 'src/components/badge-list';
import type { LabelledIdArray } from 'src/rbac/types';

import { FREQUENCY_KEY } from '@/utils/scheduler/frequencyKeys';
import { useGetAssignedUsersFieldConfig } from '@/utils/table/hooks/useGetAssignedUsersFieldConfig';
import { useGetOwnersFieldConfig } from '@/utils/table/hooks/useGetOwnersFieldConfig';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { LinkItem } from '@/utils/table/hooks/useLinkArrayField';
import { useLinkArrayField } from '@/utils/table/hooks/useLinkArrayField';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { controlDetailsUrl, riskDetailsUrl } from '@/utils/urls';

/**
 * `allOwners` / `allAssignedUsers` are named for the shared owner-column hooks,
 * which key their filtering and sorting off those exact field names.
 */
export type SchedulerRegisterTableFields = {
  Id: string;
  Name: string;
  TaskTypes: string[];
  allOwners: LabelledIdArray;
  LinkedObjects: LinkItem[];
  allAssignedUsers: LabelledIdArray;
  StartDate: null | string;
  DueDate: null | string;
  NextScheduleDate: null | string;
  Status: ScheduleStatus;
  StatusLabelled: string;
  Frequency: string;
};

/**
 * Labels and badge colours both come from the `ratings` namespace, so a tenant
 * can override either through `mergeCustomI18n`.
 */
export const SCHEDULE_STATUS_RATING_KEY = 'schedule_status';

/**
 * What the Frequency column shows for a rule the read could not parse. Shared
 * with the ribbon, whose Recurring tile has to exclude it — an unparseable rule
 * is not recurring.
 */
export const UNKNOWN_FREQUENCY_LABEL = '-';

/** Shared by the Frequency column and the ribbon's Recurring tile. */
export const useSchedulerOneOffLabel = (): string => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'scheduler.frequencies',
  });

  return st('one_off');
};

/**
 * Order matters. A rule the read could not parse arrives with both
 * `Frequency: null` and `IsRecurring: false`, so testing recurrence first would
 * label every broken rule "One-off". `IsRecurring` then separates a genuine
 * one-off from a monthly schedule, since a one-off is stored as
 * `FREQ=MONTHLY;COUNT=1` and arrives as `MONTHLY`/`1`.
 */
const useSchedulerFrequencyLabel = (): ((
  schedule: Pick<
    SchedulerScheduleRegisterItem,
    'Frequency' | 'Interval' | 'IsRecurring'
  >
) => string) => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'scheduler.frequencies',
  });
  const oneOffLabel = useSchedulerOneOffLabel();

  return useCallback(
    ({ Frequency, Interval, IsRecurring }) => {
      if (Frequency === null) {
        return UNKNOWN_FREQUENCY_LABEL;
      }
      if (!IsRecurring) {
        return oneOffLabel;
      }

      return st(FREQUENCY_KEY[Frequency], { count: Interval });
    },
    [st, oneOffLabel]
  );
};

const toIsoDate = (date: Date | null): null | string =>
  date ? date.toISOString() : null;

const targetLink = (
  target: SchedulerScheduleRegisterItem['targets'][number]
): LinkItem | null => {
  if (!target.Title) {
    return null;
  }
  switch (target.TargetType) {
    case ParentTypes.Risk:
      return { label: target.Title, url: riskDetailsUrl(target.TargetId) };
    case ParentTypes.Control:
      return { label: target.Title, url: controlDetailsUrl(target.TargetId) };
    default:
      return { label: target.Title };
  }
};

const useLabelledFields = (
  records: SchedulerScheduleRegisterItem[] | undefined
): SchedulerRegisterTableFields[] => {
  const { getLabel } = useRating(SCHEDULE_STATUS_RATING_KEY);
  const frequencyLabel = useSchedulerFrequencyLabel();

  return useMemo(
    () =>
      (records ?? []).map((record) => ({
        Id: record.Id,
        Name: record.Name,
        TaskTypes: [
          ...new Set(record.targets.map((target) => target.TypeLabel)),
        ],
        allOwners: [
          ...record.owners.map((owner) => ({
            id: owner.UserId,
            label: owner.user?.FriendlyName ?? '',
          })),
          ...record.ownerGroups.map((ownerGroup) => ({
            id: ownerGroup.UserGroupId,
            label: ownerGroup.group?.Name ?? '',
          })),
        ],
        LinkedObjects: record.targets
          .map(targetLink)
          .filter((link): link is LinkItem => link !== null),
        allAssignedUsers: [
          ...record.assigneeUsers.map((user) => ({
            id: user.UserId,
            label: user.FriendlyName ?? '',
          })),
          ...record.assigneeGroups.map((group) => ({
            id: group.UserGroupId,
            label: group.Name,
          })),
        ],
        StartDate: toIsoDate(
          record.CurrentWindowStart ??
            record.NextWindowStart ??
            record.StartDate
        ),
        DueDate: toIsoDate(record.CurrentWindowDue ?? record.NextWindowDue),
        NextScheduleDate: toIsoDate(record.NextWindowStart),
        Status: record.Status,
        StatusLabelled: getLabel(record.Status),
        Frequency: frequencyLabel(record),
      })),
    [records, getLabel, frequencyLabel]
  );
};

const getLinkedObjects = (record: SchedulerRegisterTableFields) =>
  record.LinkedObjects;

/** A schedule carries no custom attributes; hoisted so `fields` stays stable. */
const NO_CUSTOM_ATTRIBUTE_FORMS: Parent_Type_Enum[] = [];

const useGetFieldConfig = (): TableFields<SchedulerRegisterTableFields> => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'scheduler.columns',
  });
  const linkedObjects = useLinkArrayField<SchedulerRegisterTableFields>(
    st('linked_objects'),
    getLinkedObjects
  );
  const allOwners = useGetOwnersFieldConfig<SchedulerRegisterTableFields>({
    header: st('schedule_owners'),
  });
  const allAssignedUsers =
    useGetAssignedUsersFieldConfig<SchedulerRegisterTableFields>(
      st('task_owners')
    );
  const { getByValue, options: statusOptions } = useRating(
    SCHEDULE_STATUS_RATING_KEY
  );

  return useMemo(
    () => ({
      Name: {
        header: st('name'),
        cell: (item) => item.Name,
        maxWidth: MAX_COL_WIDTH,
        isRowHeader: true,
      },
      TaskTypes: {
        header: st('task_type'),
        cell: (item) =>
          item.TaskTypes.length ? <BadgeList badges={item.TaskTypes} /> : '-',
        exportVal: (item) => item.TaskTypes.join(', '),
      },
      allOwners: { ...allOwners, maxWidth: MAX_COL_WIDTH },
      LinkedObjects: linkedObjects,
      allAssignedUsers: { ...allAssignedUsers, maxWidth: MAX_COL_WIDTH },
      StartDate: dateColumnFromConfig({
        header: { header: st('start_date') },
        dateField: 'StartDate',
      }),
      DueDate: dateColumnFromConfig({
        header: { header: st('due_date') },
        dateField: 'DueDate',
      }),
      NextScheduleDate: dateColumnFromConfig({
        header: { header: st('next_schedule_date') },
        dateField: 'NextScheduleDate',
      }),
      StatusLabelled: {
        header: st('status'),
        cell: (item) => <SimpleRatingBadge rating={getByValue(item.Status)} />,
        // From the taxonomy, not the loaded rows — auto-extraction would offer
        // only the statuses currently on screen.
        filterOptions: {
          filteringOptions: statusOptions.map((option) => ({
            value: option.label,
          })),
        },
      },
      Frequency: {
        header: st('frequency'),
      },
    }),
    [st, linkedObjects, allOwners, allAssignedUsers, getByValue, statusOptions]
  );
};

export const useGetCollectionTableProps = (
  records: SchedulerScheduleRegisterItem[] | undefined
): TablePropsWithActions<SchedulerRegisterTableFields> => {
  const { t: st } = useTranslation(['common'], { keyPrefix: 'scheduler' });
  const fields = useGetFieldConfig();
  const labelledFields = useLabelledFields(records);

  return useGetTableProps({
    tableId: 'schedulerRegister',
    data: labelledFields,
    customAttributeFormIds: NO_CUSTOM_ATTRIBUTE_FORMS,
    entityLabel: st('entity_name'),
    emptyCollectionAction: <></>,
    enableFiltering: true,
    initialColumns: [
      'Name',
      'TaskTypes',
      'allOwners',
      'LinkedObjects',
      'allAssignedUsers',
      'StartDate',
      'DueDate',
      'NextScheduleDate',
      'StatusLabelled',
      'Frequency',
    ],
    fields,
    defaultSortingState: {
      sortingColumn: 'DueDate',
      sortingDirection: 'asc',
    },
  });
};
